import PropTypes from 'prop-types';
import { m } from 'framer-motion';
import { useState, useEffect } from 'react';
import Slider from 'react-slick';
import {useDispatch} from 'react-redux';
// @mui
import { styled } from '@mui/material/styles';
import { Card, Typography, Box, Stack,Link, Button} from '@mui/material';
import { project } from 'deck.gl';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Image from '../../components/Image';
import Iconify from '../../components/Iconify';
import { varFade } from '../../components/animate';
import { addProjectEvent } from '../../redux/slices/donation';
import { ProjectAPI } from '../../services/projects.services';
import projectURL from '../../routes/projects.json';

// ----------------------------------------------------------------------

const COLOR_LIGHTBLUE = '#F5FBFF';
const COLOR_GREEN = '#00A852';
const COLOR_RED = '#FF3D3D';
const COLOR_BLACK = '#000000';
const COLOR_BLUE = '#008CD9';
const COLOR_LIGHTGRAY = '#E6E6E6';
const COLOR_GRAY = '#747474';
const FONT_MERRIWEATHER = '"Merriweather",serif';
const FONT_LATO = '"Lato",sans-serif';

const s3URL = 'https://charityph-website-content.s3.ap-southeast-1.amazonaws.com';
const projectImagess3URL = 'https://charity-project-images.s3.ap-southeast-1.amazonaws.com';

const RootStyle = styled('div')(({ theme }) => ({
  minHeight: '100%',
  backgroundColor: COLOR_LIGHTBLUE,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(5),
  '& .slick-slider.slick-initialized': {
    '& .slick-prev': {
      left: '-50px',
      zIndex: 1,
    },
    '& .slick-next': {
      right: '-50px',
    },
    '& .slick-list': {
      margin: '0 -10px',
      '& .slick-track': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        '& .slick-slide': {
          margin: '0 10px',
        },
      },
    },
  },
}));

const charityDescriptionCss = {
  px: 2.5,
  typography: 'body2',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: { xs: '6', md: '3' },
  WebkitBoxOrient: 'vertical',
};

ProjectSnippets.propTypes = {
  projectContent: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    image: PropTypes.string,
    mainCause: PropTypes.string,
    location: PropTypes.string,
    description: PropTypes.string,
    buttonText: PropTypes.string,
    isSpecialProject: PropTypes.bool,
  }),
};

function ProjectSnippets({ projectContent }) {
  const { _id, name, mainImage, mainCause, location, shortDescription, isSpecialProject, buttonText } = projectContent;  
  const projectHref = projectURL[_id] ?? `project-details/${_id}`;

  const dispatch = useDispatch();
  return (
    <>      
      <Card
        sx={{
          width: '100%',
          minHeight: { xs: '100%', md: isSpecialProject ? 450 : 480 },
          height: { xs: '100%', md: isSpecialProject ? 440 : 480 },
          boxShadow: 3,
          borderRadius: 1,
          color: '#000',
          cursor: 'default',
          transition: '0.7s ease',
          '&:hover': {
            boxShadow: 24,
          },
        }}
      >
        <Image
          sx={{
            height: { xs: 200, md: 170 },
            width: '100%',
          }}
          alt={name}
          src={`${mainImage.includes('https') ? mainImage : mainImage.includes('/images/projects') ? s3URL + mainImage : projectImagess3URL + mainImage}` /* eslint-disable-line no-nested-ternary */}

        />
        {/* <Chip
          label={mainCause}
          color="primary"
          sx={{
            backgroundColor: 'rgb(0,0,0, .7)',
            position: 'absolute',
            right: 5,
            top: 7,
            textTransform: 'capitalize !important',
          }}
        /> */}
        <Stack direction={'column'} height="inherit" width="inherit" spacing={1}>
          <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  padding: '8px 16px',
                  color: COLOR_GREEN,
                  textAlign: 'right',
                  textTransform: 'capitalize',                
                }}
              >
            {mainCause}
          </Typography>
          <Typography
            component={Link}
            variant="h6"
            fontFamily={FONT_MERRIWEATHER}
            href={projectHref}
            sx={{
              px: 2.5,
              marginTop: '0px !important',
              color: '#000000',
              '&:hover': { color: COLOR_BLUE },
            }}
          >
            {name}
          </Typography>
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, color: COLOR_GRAY, px: 2.5, pb: 1, textTransform: 'capitalize' }}
          >
            Project Location:{' '}
            <Typography
              component={'span'}
              variant="overline"
              sx={{ fontWeight: 700, textTransform: 'capitalize', color: COLOR_BLACK }}
            >
              {location}
            </Typography>
          </Typography>
          <Typography variant="body2" sx={charityDescriptionCss}>
            {shortDescription}
          </Typography>
          {!isSpecialProject ? (
            <Box
              component="div"
              sx={{ width: 1, px: 2.5, pt: 2, position: { md: 'absolute' }, bottom: { md: 25 }, pb: { xs: 3, md: 0 } }}
            >
              <Button
                fullWidth
                variant="contained"                
                onClick={() => {                
                  dispatch(addProjectEvent(projectContent));
                  window.location.href = `/donation`;                                  
                }}
              >
                Donate
              </Button>
            </Box>
          ) : null}
        </Stack>
      </Card>
    </>
    
  );
}

const PrevArrow = (props) => {
  const { className, onClick } = props;
  return (
    <Iconify
      className={className}
      onClick={onClick}
      icon={'akar-icons:chevron-left'}
      sx={{ width: 40, height: 40, color: COLOR_GRAY, '&:hover': { color: COLOR_GRAY } }}
    />
  );
};
const NextArrow = (props) => {
  const { className, onClick } = props;
  return (
    <Iconify
      className={className}
      onClick={onClick}
      icon={'akar-icons:chevron-right'}
      sx={{ width: 40, height: 40, color: COLOR_GRAY, '&:hover': { color: COLOR_GRAY } }}
    />
  );
};

const sliderSetting = {
  arrows: true,
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 3,
  swipeToSlide: true,
  prevArrow: <PrevArrow />,
  nextArrow: <NextArrow />,
};

CharityProject.propTypes = {
  charity: PropTypes.object,
};

export default function CharityProject({ charity }) {

  // const queryString = window.location.search;
  // const urlParams = new URLSearchParams(queryString);
  // const id = urlParams.get('id');

  const { _id, firstName } = charity;
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const mdUp = useResponsive('up', 'md'); 
  // const totalProjects = projects.length;
 
  async function getProjects() {
    if(charity && charity.projects){
      setProjects(charity.projects.filter(p => p.isActive === true && p.deleted !== true));
    }
  }

  useEffect(() => {
    getProjects();
    setIsLoading(false);
    return () => {
      setProjects([]);
    };
  }, []);

  return (
    <RootStyle>
      {isLoading === false ? (
        <>          
          {projects.length > 0 ? (
            <Typography variant="h6" mb={4} fontFamily={FONT_LATO}>
              {`PROJECTS (${projects.length})`}
            </Typography>
          ) : null}

          <m.div variants={varFade().inUp}>
            {mdUp ? (
              <Slider {...sliderSetting}>
                {projects.map((content) => (
                  <Box width={372} key={content.id}>
                    <ProjectSnippets projectContent={content} />
                  </Box>
                ))}
              </Slider>
            ) : (
              <Stack spacing={2}>
                {projects.map((content) => (
                  <Box width={'100%'} key={content.id}>
                    <ProjectSnippets projectContent={content} />
                  </Box>
                ))}
              </Stack>
            )}
          </m.div>
        </>
      ) : null}
    </RootStyle>
  );
}
